/*
 * Copyright (c) 2025. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
 * filterregel-edit.component.ts
 * Gegenereerd met Junie (AI), 29-09-2025
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Abstracte superclass voor alle filterregel-editorcomponenten.
 */

import {Directive, EventEmitter, Input, Output} from '@angular/core';
import {BaseComponent} from '../../../components/basecomponent';
import {Filtertoolregel} from '../../../services/filtertool/filtertool-regels';

/**
 * Basisklasse voor editorcomponenten van filterregels. Biedt een consistente API voor Inputs/Outputs
 * en gemeenschappelijke hulpfuncties voor wijzigen en verwijderen.
 */
@Directive()
export abstract class FilterregelEditComponent<TRegel extends Filtertoolregel> extends BaseComponent {
  // Inputs (properties)
  /** De te bewerken regel. Subclasses specialiseren het type. */
  @Input({ required: true }) regel!: TRegel;
  /** De id van de omliggende clausule (node in de boom). Nodig voor correct verwijderen. */
  @Input() clausuleId?: string;
  /** True wanneer de regel direct onder de root-groep valt. */
  @Input() isInRootGroep: boolean = false;
  /**
   * Css-selector van de container waarin de keuzelijsten van deze editor geplaatst worden. Komt van
   * het scherm (de body van het window), zodat een lijst niet wordt afgeknipt door de scrollende
   * inhoud en toch met het window meeschuift.
   */
  @Input() dropdownContainer: string = 'body';

  // Outputs
  /** Event met de volledige, bijgewerkte regel na een wijziging (optioneel, voor dirty-state). */
  @Output() wijzig = new EventEmitter<TRegel>();
  /** Event met het id van de clausule die verwijderd moet worden. */
  @Output() verwijder = new EventEmitter<string>();
  /** Verzoekt om de regel één niveau omhoog te verplaatsen (uit de huidige groep). */
  @Output() uitDezeGroep = new EventEmitter<string>();
  /** Verzoekt om de regel in een EN-groep te wikkelen. */
  @Output() wikkelInEn = new EventEmitter<string>();
  /** Verzoekt om de regel in een OF-groep te wikkelen. */
  @Output() wikkelInOf = new EventEmitter<string>();

  // Properties

  // Constructor
  public constructor(loglevel: string = "warn") {
    super(loglevel);
  }

  // Public methods
  /**
   * Geeft terug of de huidige regel geldig is volgens de validatieregels.
   */
  public get isRegelGeldig(): boolean {
    this.log.debug('isRegelGeldig', this.regel, typeof this.regel);
    return !!(this.regel && (this.regel as any).valid);
  }

  /**
   * Emit een wijziging met een kopie van de huidige regel, aangevuld met de opgegeven patch.
   */
  public emitWijziging(patch: Partial<TRegel>): void {
    if (!this.regel) {
      return;
    }
    const nieuw: TRegel = { ...(this.regel as any), ...(patch as any) } as TRegel;
    this.wijzig.emit(nieuw);
  }

  /**
   * Emit een verwijderverzoek voor de huidige clausule (regel-node) op basis van de clausuleId.
   * Als die ontbreekt, valt terug op het regel-id (best effort).
   */
  public verzoekVerwijderen(): void {
    const id = this.clausuleId ?? this.regel?.id;
    if (id) {
      this.verwijder.emit(id);
    }
  }
}
